/* -------------------------------------------
 * First-interaction helper (consume global, return cleanup)
 * ----------------------------------------- */
const onFirstInteract = (cb, timeoutMs = 8000, events = ['pointerdown','keydown','scroll','touchstart']) => {
  if (typeof window.__onFirstInteract === 'function') {
    return window.__onFirstInteract(cb, timeoutMs, events);
  }

  let ran = false;
  const run = () => { if (ran) return; ran = true; try { cb(); } catch {} cleanup(); };

  const opts = { once: true, passive: true };
  events.forEach(ev => addEventListener(ev, run, opts));
  const timer = setTimeout(run, timeoutMs);

  const cleanup = () => {
    events.forEach(ev => removeEventListener(ev, run, opts));
    clearTimeout(timer);
  };

  return cleanup;
};

/* -------------------------------------------
 * <linked-products> web component
 * ----------------------------------------- */
class LinkedProducts extends HTMLElement {
  constructor() {
    super();
    this._onMouseOver = this.mouseOver.bind(this);
    this._onMouseLeave = this.mouseLeave.bind(this);
  }

  connectedCallback() {
    this.items = this.querySelectorAll('[data-label]');
    this.currentLabel = this.querySelector('[data-current-label]');
    this.originalLabel = this.currentLabel?.textContent || '';

    this.addEventListener('mouseover', this._onMouseOver);
    this.addEventListener('mouseleave', this._onMouseLeave);
  }

  disconnectedCallback() {
    this.removeEventListener('mouseover', this._onMouseOver);
    this.removeEventListener('mouseleave', this._onMouseLeave);
  }

  /**
   * @param {MouseEvent} event - The mouse event object
   */
  mouseOver(event) {
    const target = event.target;
    if (target && target.hasAttribute('data-label') && this.currentLabel) {
      this.currentLabel.textContent = target.getAttribute('data-label') || '';
    }
  }

  mouseLeave() {
    if (this.currentLabel) {
      this.currentLabel.textContent = this.originalLabel;
    }
  }
}

if (!window.customElements.get('linked-products')) {
  window.customElements.define('linked-products', LinkedProducts);
}

/* -------------------------------------------
 * Rotating header search placeholders
 * ----------------------------------------- */
function initHeaderSearchPlaceholders() {
  const placeholderNode = document.querySelector('[data-search-placeholder]');
  if (!placeholderNode) return;

  let placeholders = [];
  try {
    placeholders = JSON.parse(placeholderNode.textContent || '[]');
  } catch {
    return;
  }
  if (!Array.isArray(placeholders) || placeholders.length === 0) return;

  const headerSearchInputs = document.querySelectorAll('[data-header-search-input]');
  if (!headerSearchInputs.length) return;

  let currentIndex = 0;
  let charIndex = 0;
  let inputFieldActive = false;
  const typingSpeed = 150;
  const pauseTime = 500;

  headerSearchInputs.forEach((headerSearchInput) => {
    if (headerSearchInput.tagName.toLowerCase() !== 'span') {
      headerSearchInput.addEventListener('focus', () => { 
        inputFieldActive = true; 
      }, { passive: true });
      headerSearchInput.addEventListener('blur', () => { 
        inputFieldActive = false; 
      }, { passive: true });
    }
  });

  const typeEffect = () => {
    if (currentIndex >= placeholders.length) currentIndex = 0;
    const fullText = String(placeholders[currentIndex] ?? '');
    const currentText = fullText.substring(0, charIndex++);

    headerSearchInputs.forEach((headerSearchInput) => {
      if (headerSearchInput.tagName.toLowerCase() === 'span') {
        headerSearchInput.textContent = currentText;
      } else {
        headerSearchInput.placeholder = inputFieldActive ? '' : currentText;
      }
      
      if (charIndex === fullText.length + 1) {
        headerSearchInput.classList.remove('show');
      } else {
        headerSearchInput.classList.add('show');
      }
    });

    if (charIndex === fullText.length + 1) {
      charIndex = 0;
      currentIndex++;
      setTimeout(typeEffect, pauseTime);
    } else {
      setTimeout(typeEffect, typingSpeed);
    }
  };

  typeEffect();
}

/* -------------------------------------------
 * Transparent header height fix (conservative)
 * ----------------------------------------- */
function initTransparentHeaderHeightFix() {
  const firstSection = document.querySelector('.shopify-section:first-child');
  const header = document.querySelector('header');
  
  if (
    firstSection &&
    firstSection.querySelector('[allow-transparent-header]') &&
    header &&
    header.querySelector('.predictive-search-form--container')
  ) {
    header.style.height = 'auto';
  }
}

/* -------------------------------------------
 * <cart-discount-field> web component
 * ----------------------------------------- */
class CartDiscountField extends HTMLElement {
  constructor() {
    super();
    this._onClickApply = this.applyDiscount.bind(this);
    this._onKeyDown = this.handleKeyDown.bind(this);
    this._onRemoveClick = this.removeDiscount.bind(this);
  }

  connectedCallback() {
    this.input = this.querySelector('input');
    this.button = this.querySelector('button');

    if (this.button) this.button.addEventListener('click', this._onClickApply);
    if (this.input) this.input.addEventListener('keydown', this._onKeyDown);

    this.removeDiscountButtons = this.querySelectorAll('.remove-discount-btn');
    this.removeDiscountButtons.forEach(button => {
      button.addEventListener('click', this._onRemoveClick);
    });
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this._onClickApply);
    if (this.input) this.input.removeEventListener('keydown', this._onKeyDown);
    this.removeDiscountButtons?.forEach(button => {
      button.removeEventListener('click', this._onRemoveClick);
    });
  }

  handleKeyDown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.applyDiscount();
    }
  }

  async updateCart(discountCodeString) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ discount: discountCodeString })
      });
      
      if (!response.ok) throw new Error('Failed to update discount');
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (error) {
      console.error('Error updating discount:', error);
    }
  }

  async applyDiscount() {
    const discountCode = this.input.value.trim();
    const discountCodes = Array.from(this.removeDiscountButtons).map(button => 
      button.getAttribute('data-discount-code')
    );

    if (discountCode) {
      discountCodes.push(discountCode);
    }
    
    const uniqueCodes = [...new Set(discountCodes)];
    const discountCodeString = uniqueCodes.join(',');

    await this.updateCart(discountCodeString);
  }

  async removeDiscount(event) {
    const buttonElement = (event.currentTarget) || event.target?.closest('button');
    const discountCode = buttonElement?.getAttribute('data-discount-code');
    if (!discountCode) return;

    const discountCodes = Array.from(this.removeDiscountButtons)
      .map(button => button.getAttribute('data-discount-code'))
      .filter(Boolean)
      .filter(c => c !== discountCode);

    const uniqueCodes = [...new Set(discountCodes)];
    await this.updateCart(uniqueCodes.join(','));
  }
}

if (!window.customElements.get('cart-discount-field')) {
  window.customElements.define('cart-discount-field', CartDiscountField);
}

/* -------------------------------------------
 * <offers-slider> web component
 * ----------------------------------------- */
class OffersSlider extends HTMLElement {
  connectedCallback() {
    this._slides = Array.from(this.querySelectorAll('.offers-slider__slide'));
    this._dots = Array.from(this.querySelectorAll('.offers-slider__dots button'));
    this._current = 0;
    this._speed = parseInt(this.dataset.autoplay, 10) || 5000;
    this._timer = null;

    this._dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        clearInterval(this._timer);
        this._goTo(i);
        this._start();
      });
    });

    this._goTo(0);
    if (this._slides.length > 1) this._start();
  }

  disconnectedCallback() {
    clearInterval(this._timer);
  }

  _goTo(index) {
    this._slides[this._current].classList.remove('is-active');
    if (this._dots[this._current]) this._dots[this._current].setAttribute('aria-current', 'false');
    this._current = ((index % this._slides.length) + this._slides.length) % this._slides.length;
    this._slides[this._current].classList.add('is-active');
    if (this._dots[this._current]) this._dots[this._current].setAttribute('aria-current', 'true');
  }

  _start() {
    this._timer = setInterval(() => this._goTo(this._current + 1), this._speed);
  }
}

if (!window.customElements.get('offers-slider')) {
  window.customElements.define('offers-slider', OffersSlider);
}

/* -------------------------------------------
 * Public lazy entrypoint (used in <script type="module"> import('custom')…)
 * ----------------------------------------- */
export function initLazyFeatures() {
  onFirstInteract(() => {
    // Import optional/behavioral features only after first interaction
    // If these files don't exist in some themes, Promise.allSettled prevents crashing
    Promise.allSettled([
      import('./free-gifts-component.js'),
      import('./collapsible-text.js'),
      // Add more optional modules here as needed…
    ]);
  }, 4000);
}

/* -------------------------------------------
 * Boot small, safe, non-blocking initializers
 * ----------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  initHeaderSearchPlaceholders();
  initTransparentHeaderHeightFix();
});




/* -------------------------------------------
 * Keep hover active until item or mega menu is not hovered
 * ----------------------------------------- */
(function () {
  const MQ = window.matchMedia("(min-width: 1000px)");

  function initMegaMenuSiblingPanels(root) {
    if (!root) return;

    const level2Items = Array.from(root.querySelectorAll(".mega-menu__level2-item"));
    const panels = Array.from(root.querySelectorAll(".mega-menu__level3-panel"));

    if (!level2Items.length) return;

    const itemsWithChildren = level2Items.filter((el) => el.classList.contains("has-children"));

    const clearActive = () => {
      level2Items.forEach((el) => el.classList.remove("is-active"));
      panels.forEach((p) => p.classList.remove("is-active"));
    };

    const setActiveByPanelId = (panelId) => {
      // Set active state on L2
      level2Items.forEach((el) => {
        el.classList.toggle("is-active", el.getAttribute("data-panel-id") === panelId);
      });

      // Set active state on panel
      panels.forEach((p) => {
        p.classList.toggle("is-active", p.getAttribute("data-panel-id") === panelId);
      });
    };

    const setDefaultActive = () => {
      const alreadyActive = root.querySelector(".mega-menu__level2-item.is-active");
      if (alreadyActive) return;
      if (!itemsWithChildren.length) return;

      const panelId = itemsWithChildren[0].getAttribute("data-panel-id");
      if (panelId) setActiveByPanelId(panelId);
    };

    level2Items.forEach((item) => {
      const hasChildren = item.classList.contains("has-children");
      const panelId = item.getAttribute("data-panel-id");

      item.addEventListener("mouseenter", () => {
        // IMPORTANT: hovering any L2 item without children clears/hides L3
        if (!hasChildren) return clearActive();
        if (panelId) setActiveByPanelId(panelId);
      });

      item.addEventListener("focusin", () => {
        if (!hasChildren) return clearActive();
        if (panelId) setActiveByPanelId(panelId);
      });
    });

    // Keep open while inside mega menu, clear when leaving
    root.addEventListener("mouseenter", setDefaultActive);
    root.addEventListener("mouseleave", clearActive);
  }

  function boot() {
    if (!MQ.matches) return;

    document.querySelectorAll(".mega-menu--l2-l3").forEach((menu) => {
      if (menu.dataset.mmSiblingPanels === "true") return;
      menu.dataset.mmSiblingPanels = "true";
      initMegaMenuSiblingPanels(menu);
    });
  }

  document.addEventListener("DOMContentLoaded", boot);
  document.addEventListener("shopify:section:load", boot);
  MQ.addEventListener?.("change", boot);
})();